/**
 * sl-ufq6.3.2: Automation Budget Group Selector
 *
 * Dropdown for assigning an automation to a budget group.
 * Persists selection via REST API.
 */
import { notify } from '@affine/component';
import { workspaceApiFetch } from '@affine/core/modules/cloud';
import { type ChangeEvent, useCallback, useEffect, useState } from 'react';

interface GroupOption {
  id: string;
  name: string;
  isAutoSync: boolean;
}

export const AutomationGroupSelector = ({
  workspaceId,
  automationBlockId,
}: {
  workspaceId: string;
  automationBlockId?: string;
}) => {
  const [groups, setGroups] = useState<GroupOption[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [loading, setLoading] = useState(true);

  const budgetBase = `/api/workspace/${workspaceId}/ai/budget-limits`;

  useEffect(() => {
    if (!workspaceId || !automationBlockId) {
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        const [groupResp, budgetResp, currentResp] = await Promise.all([
          workspaceApiFetch(
            `/api/workspace/${workspaceId}/groups`,
            {
              credentials: 'include',
            },
            backgroundPolicy(workspaceId, 'automation-groups')
          ),
          workspaceApiFetch(
            budgetBase,
            { credentials: 'include' },
            backgroundPolicy(workspaceId, 'automation-budget-limits')
          ),
          workspaceApiFetch(
            `${budgetBase}/automation-group/${automationBlockId}`,
            {
              credentials: 'include',
            },
            backgroundPolicy(
              workspaceId,
              `automation-group:${automationBlockId}`
            )
          ),
        ]);

        if (groupResp.ok) {
          const gData = await groupResp.json();
          const allGroups: GroupOption[] = Array.isArray(gData)
            ? gData
            : (gData.groups ?? []);
          // Only show non-autoSync groups that have a budget limit configured
          if (budgetResp.ok) {
            const bData = await budgetResp.json();
            const limitedGroupIds = new Set(
              (bData.groupLimits ?? []).map(
                (gl: { groupId: string }) => gl.groupId
              )
            );
            setGroups(
              allGroups.filter(g => !g.isAutoSync && limitedGroupIds.has(g.id))
            );
          }
        }

        if (currentResp.ok) {
          const cData = await currentResp.json();
          const ids: string[] = cData.groupIds ?? [];
          setSelectedGroupId(ids[0] ?? '');
        }
      } catch {
        // silently fail
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [workspaceId, automationBlockId, budgetBase]);

  const handleGroupChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      const groupId = e.target.value;
      if (!automationBlockId) return;

      const previousGroupId = selectedGroupId;
      setSelectedGroupId(groupId);

      const request = groupId
        ? workspaceApiFetch(
            `${budgetBase}/automation-group/${automationBlockId}`,
            {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ groupId }),
            },
            foregroundPolicy(
              workspaceId,
              `automation-group-update:${automationBlockId}`
            )
          )
        : workspaceApiFetch(
            `${budgetBase}/automation-group/${automationBlockId}`,
            {
              method: 'DELETE',
              credentials: 'include',
            },
            foregroundPolicy(
              workspaceId,
              `automation-group-delete:${automationBlockId}`
            )
          );

      void request
        .then(async response => {
          if (!response.ok) throw new Error(await response.text());
        })
        .catch(error => {
          setSelectedGroupId(previousGroupId);
          notify.error({
            title: 'Budget group could not be updated',
            message: error instanceof Error ? error.message : undefined,
          });
        });
    },
    [automationBlockId, budgetBase, selectedGroupId, workspaceId]
  );

  if (loading || groups.length === 0 || !automationBlockId) {
    return null;
  }

  return (
    <div
      style={{
        padding: '8px 12px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 13,
        color: 'var(--streamlain-text-secondary-color)',
      }}
    >
      <span style={{ whiteSpace: 'nowrap' }}>Budget-Gruppe:</span>
      <select
        value={selectedGroupId}
        onChange={handleGroupChange}
        style={{
          flex: 1,
          padding: '4px 8px',
          borderRadius: 4,
          border: '1px solid var(--streamlain-border-color)',
          background: 'var(--streamlain-background-primary-color)',
          color: 'var(--streamlain-text-primary-color)',
          fontSize: 13,
        }}
      >
        <option value="">Alle Automationen (Standard)</option>
        {groups.map(g => (
          <option key={g.id} value={g.id}>
            {g.name}
          </option>
        ))}
      </select>
    </div>
  );
};

function backgroundPolicy(workspaceId: string, key: string) {
  return {
    workspaceId,
    category: 'settings' as const,
    key,
    mode: 'background' as const,
    priority: 'background' as const,
  };
}

function foregroundPolicy(workspaceId: string, key: string) {
  return {
    workspaceId,
    category: 'foreground-action' as const,
    key,
    mode: 'foreground' as const,
    priority: 'foreground' as const,
  };
}
